import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "./tourdetails.css";
import { io } from "socket.io-client";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const safeJSON = (str, fallback) => {
    try { return str ? JSON.parse(str) : fallback; } catch { return fallback; }
};
const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
};
const fmtTime = (t) => (t ? String(t).slice(0, 5) : "—");
const fmtMoney = (n) => Number(n || 0).toLocaleString("ru-RU");
const getInitials = (s) =>
    (s || "?").trim().split(/\s+/).map(w => w[0]).join("").toUpperCase().slice(0, 2);

const PAY_LABEL = { cash: "Наличные", debt: "Долг", transfer: "Перечисление", terminal: "Терминал" };
const PAY_ICON = { cash: "fa-money-bill-wave", debt: "fa-credit-card", transfer: "fa-university", terminal: "fa-credit-card" };

const TYPE_BADGE = {
    individual: { label: "Индивидуальный", color: "#3b82f6" },
    sharetour: { label: "Share Tour", color: "#8b5cf6" },
    partners: { label: "Партнёры", color: "#f97316" },
};

const detectType = (data) => {
    if (!data) return null;
    if ("company" in data) return "partners";
    if ("departure_place" in data) return "individual";
    return "sharetour";
};

const Field = ({ label, value, bold, accent, orange, span, title }) => {
    const cls = [
        "td-val",
        bold && "td-val--bold",
        accent && "td-val--accent",
        orange && "td-val--orange",
        (value == null || value === "") && "td-val--empty",
    ].filter(Boolean).join(" ");

    return (
        <div className={span ? "td-f--span" : undefined}>
            <div className="td-label">{label}</div>
            <div title={title} className={cls}>{value == null || value === "" ? "—" : value}</div>
        </div>
    );
};

const Card = ({ faIcon, title, children }) => (
    <div className="td-card">
        <div className="td-card-head">
            <i className={`fas ${faIcon}`} />
            {title}
        </div>
        {children}
    </div>
);

// Кол-во машин = "Кол-во" в строке "Машина" стоимости услуг (так же считает бэкенд для назначения водителей)
const carsCount = (services) => {
    const car = services.find((s) => s.title === "Машина");
    return Number(car?.qty) > 0 ? Number(car.qty) : null;
};

function Card1({ type, order, guests, food, services }) {
    const g0 = guests[0] || {};
    const foodLabel = [
        food.standard > 0 && `Стандарт (${food.standard})`,
        food.vegan > 0 && `Веган (${food.vegan})`,
        food.other > 0 && `Другое (${food.other})`,
    ].filter(Boolean).join(" / ") || "—";

    if (type === "partners") {
        return (
            <Card faIcon="fa-building" title="Компания">
                <div className="td-g2">
                    <Field label="КОМПАНИЯ" value={order.company} bold span />
                    <Field label="КОНТАКТ КОМПАНИИ" value={order.customer_contacts} span />
                    <Field label="ТУР ОПЕРАТОР" value={order.tour_operator} />
                    <Field label="КОЛ-ВО ГОСТЕЙ" value={order.participants_count ? `${order.participants_count} чел.` : null} />
                    <Field label="ТИП МАШИНЫ" value={order.transport_type} />
                    <Field label="КОЛ-ВО МАШИН" value={carsCount(services)} />
                    <Field label="ТИП ПИТАНИЯ" value={foodLabel} />
                </div>
            </Card>
        );
    }

    return (
        <Card faIcon="fa-user" title="Заказчик">
            <div className="td-g2">
                <Field label="ИМЯ" value={g0.name} bold />
                <Field label="ТУР ОПЕРАТОР" value={order.tour_operator} />
                <Field title={order.tour} label="ПРОГРАММА" value={order.tour} span />
                <Field label="КОЛ-ВО ГОСТЕЙ" value={order.participants_count ? `${order.participants_count} чел.` : null} />
                <Field label="ТИП МАШИНЫ" value={order.transport_type} />
                <Field label="КОЛ-ВО МАШИН" value={carsCount(services)} />
                <Field label="ТИП ПИТАНИЯ" value={foodLabel} />
            </div>
        </Card>
    );
}

// ─── Card 3: Контакт и группа ─────────────────────────────────────────────────
function Card3({ type, order, guests }) {
    const g0 = guests[0] || {};

    if (type === "partners") {
        return (
            <Card faIcon="fa-envelope" title="Контакт и группа">
                <div className="td-g2">
                    <Field label="ИМЯ ГОСТЯ" value={g0.name} bold />
                    <Field label="КОНТАКТ КЛИЕНТА" value={g0.contacts} />
                    <Field label="№ ГРУППЫ" value={order.group_number} orange />
                    <Field label="МЕНЕДЖЕР" value={order.manager} />

                </div>
            </Card>
        );
    }

    return (
        <Card faIcon="fa-envelope" title="Контакт и группа">
            <div className="td-g2">
                <Field label="ИМЯ КОНТАКТА" value={g0.name} bold span />
                <Field label="Контакты" value={g0.contacts} />
                <Field label="МЕНЕДЖЕР" value={order.manager} />
            </div>
        </Card>
    );
}

// ─── О Программе ──────────────────────────────────────────────────────────────
// sharetour: departure у каждого гостя → здесь только гид / музей / локация
function ProgramSection({ type, order, food }) {
    const vegan = (food.vegan > 0 && food.other > 0)
        ? `Веган (${food.vegan}) + Другое (${food.other})`
        : (food.vegan > 0 && food.other === 0)
            ? `Веган (${food.vegan})`
            : (food.vegan === 0 && food.other > 0)
                ? `Другое (${food.other})`
                : null;

    const fields = type === "sharetour"
        ? [
            ["ТУР ГИД", order.guidetype === 1
                ? order.guide === null
                    ? "не выбран гид"
                    : order.guide
                : "нет"],
            ["МУЗЕЙ", order.museum],
            ["ДОП. ЛОКАЦИЯ", order.extra_location],
            ["СПЕЦ. ПИТАНИЕ", vegan],
            ["Кафе", order.cafe]
        ]
        : [
            ["МЕСТО ВЫЕЗДА", order.departure_place],
            ["ВРЕМЯ ВЫЕЗДА", fmtTime(order.departure_time)],
            ["МЕСТО ВЫСАДКИ", order.arrival_place],
            ["СПЕЦ. ПИТАНИЕ", vegan],
            ["ТУР ГИД", order.guidetype === 1
                ? order.guide === null
                    ? "не выбран гид"
                    : order.guide
                : "нет"],
            ["МУЗЕЙ", order.museum],
            ["ДОП. ЛОКАЦИЯ", order.extra_location],
            ["Кафе", order.cafe]
        ];

    return (
        <div className="td-prog">
            <div className="td-prog-head">
                <i className="fas fa-map-marker-alt" />
                О Программе
            </div>
            <div className="td-prog-grid">
                {fields.map(([lbl, val]) => (
                    <div key={lbl}>
                        <div className="td-prog-label">{lbl}</div>
                        <div className="td-prog-val">{val || "—"}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}
function computePaymentStatus(order) {
    if (order.paid_amount === null || order.paid_amount === undefined) return null;
    const paid = Number(order.paid_amount);
    const total = Number(order.grand_total) || 0;
    if (paid <= 0) return { label: "Не оплачено", color: "#EF4B4B" };
    if (paid >= total) return { label: "Оплачено", color: "#2FB871" };
    return { label: "Частично оплачено", color: "#FF9F43" };
}
// ─── Оплата ───────────────────────────────────────────────────────────────────
function PaymentSection({ order, services, extras }) {
    const paymentStatus = computePaymentStatus(order);
    return (
        <div className="td-pay">
            <div className="td-pay-head">
                <i className="fas fa-credit-card" />
                Оплата — стоимость услуг
            </div>
            <div
                className="td-svc-grid"
                style={{ gridTemplateColumns: `repeat(${Math.max(services.length, 1)}, 1fr)` }}>
                {services.map((s) => (
                    <div key={s.title} className="td-svc-grid-div">
                        <div className="td-label">{s.title}</div>
                        <div className="td-svc-amount">${fmtMoney(s.total)}</div>
                        <div className="td-svc-detail">{s.qty} × ${s.price}</div>
                    </div>
                ))}
            </div>

            {extras.length > 0 && (
                <div className="td-extras-block">
                    <div className="td-label">ДОП. УСЛУГИ</div>
                    <div className="td-extras-list">
                        {extras.map((x) => (
                            <div key={x.title} className="td-extras-block-div">
                                <div className="td-extra-title">{x.title}</div>
                                <div className="td-extra-price">${fmtMoney(x.total)}</div>
                                <div className="td-extra-qty">{x.qty} × ${x.price}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="td-pay-footer">
                <div className="td-total-box">
                    <div className="td-total-label">ТИП ОПЛАТЫ</div>
                    <div className="td-pay-type">
                        <i className={`fas ${PAY_ICON[order.payment_type] || "fa-wallet"}`} />
                        {PAY_LABEL[order.payment_type] || order.payment_type || "—"}
                    </div>
                    {Boolean(order.payment_by_date) && order.payment_date && (
                        <div className="td-svc-detail">
                            Оплата ожидается: {fmtDate(order.payment_date)}
                        </div>
                    )}
                </div>
                {paymentStatus && (
                    <div className="td-total-box">
                        <div className="td-total-label">СТАТУС ОПЛАТЫ</div>
                        <div className="td-pay-type" style={{ color: paymentStatus.color }}>
                            <i className="fas fa-circle-check" />
                            {paymentStatus.label}
                        </div>
                        <div className="td-svc-detail">
                            Получено: ${fmtMoney(order.paid_amount)} из ${fmtMoney(order.grand_total)}
                        </div>
                    </div>
                )}
                <div className="td-total-box">
                    <div className="td-total-label">Цена на одного целовека</div>
                    <div className="td-total-amt">${fmtMoney(order.grand_total / order.participants_count)}</div>
                </div>
                <div className="td-total-box">
                    <div className="td-total-label">ОБЩАЯ СУММА</div>
                    <div className="td-total-amt">${fmtMoney(order.grand_total)}</div>
                </div>
            </div>
        </div>
    );
}

// ─── Таблица гостей ───────────────────────────────────────────────────────────
// sharetour: у каждого гостя своё место / время выезда и прибытия
function GuestsTable({ type, guests }) {
    if (!guests.length) return null;
    if (type !== "sharetour") return null;
    const isST = type === "sharetour";

    const cols = ["#", "Имя", "Контакт", ...(isST ? ["Место отправления", "Время", "Место прибытия"] : [])];

    return (
        <div className="td-guests">
            <div className="td-guests-head">
                <i className="fas fa-users" />
                Список гостей
            </div>
            <table className="td-table">
                <thead>
                    <tr>{cols.map(h => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                    {guests.map((g, i) => (
                        <tr key={i}>
                            <td className="td-col-num">{i + 1}</td>
                            <td className="td-col-name">{g.name || "—"}</td>
                            <td>{g.contacts || "—"}</td>
                            {isST && <>
                                <td>{g.departurePlace || "—"}</td>
                                <td>{g.departureTime || "—"}</td>
                                <td>{g.arrivalPlace || "—"}</td>
                            </>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const TourDetails = ({ logout, user, API_URL }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(null); // "ru" | "en" | null

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const res = await axios.post(`${API_URL}/getTourDetails`, { id });
                setOrder(res.data || null);
            } catch (err) {
                console.error("Ошибка загрузки:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();

        const socketUrl = API_URL.replace(/\/api\/?$/, "");
        const socket = io(socketUrl);

        socket.on("calendar:update", (payload) => {
            // если событие явно про другой заказ — не дёргаем сеть зря
            if (payload?.orderNumber && String(payload.orderNumber) !== String(id)) return;
            fetchOrder();
        });

        return () => socket.disconnect();
    }, [API_URL, id]);

    if (loading) return (
        <div className="td-state td-state--loading">
            <i className="fas fa-spinner fa-spin" />
            Загрузка...
        </div>
    );
    if (!order) return (
        <div className="td-state td-state--error">
            <i className="fas fa-exclamation-circle" />
            Заявка не найдена
        </div>
    );

    const type = detectType(order);
    const services = safeJSON(order.services, []);
    const guests = safeJSON(order.guests, []);
    const food = safeJSON(order.food, { standard: 0, vegan: 0, other: 0 });
    const extras = safeJSON(order.extra_items, []);

    const g0 = guests[0] || {};
    const avatarStr = type === "partners" ? (order.company || "P") : (g0.name || order.manager || "?");
    const headerTitle = type === "partners" ? order.company : g0.name;
    const badge = TYPE_BADGE[type] || { label: type, color: "#6b7280" };

    const handleUpdate = async (orderNumber, status) => {
        const payload = {
            orderNumber,
            status,
            requestedBy: user?.fullname,
            requestedRole: user?.role,
        };
        const confirmDelete = window.confirm(`${status === 0 ? "Активировать?" : "Отменять?"}`);
        if (!confirmDelete) return;
        try {
            await axios.post(`${API_URL}/updateStatusTourDetails`, payload);
            if (status === 0) {
                toast.success("Изменения сохранены");

            } else {
                toast.error("Тур отменен");
            }
            setOrder(prev => ({
                ...prev,
                status,
            }));
        } catch (err) {
            console.error("Ошибка при сохранении:", err);
            toast.error("Не удалось сохранить изменения");
        }
    };
    const handlePdf = async (lang) => {
        if (pdfLoading) return;
        setPdfLoading(lang);
        try {
            // грузим react-pdf только по клику, чтобы не раздувать основной бандл
            const { downloadTourPdf } = await import("./TourPdf");
            await downloadTourPdf(order, lang);
        } catch (err) {
            console.error("Ошибка PDF:", err);
            toast.error("Не удалось сформировать PDF");
        } finally {
            setPdfLoading(null);
        }
    };

    return (
        <div className="td-root">

            {/* ── Header ── */}
            <div className="td-header">
                <div className="td-horder">
                    <span className="td-hlabel">№</span>
                    <span className="td-horder-num">#{order.order_number}</span>
                </div>
                <div className="td-avatar">{getInitials(avatarStr)}</div>
                <div className="td-vdivider" />
                {[
                    { lbl: "КЛИЕНТ", val: headerTitle },
                    { lbl: "ПРОГРАММА", val: order.tour?.length > 22 ? order.tour.slice(0, 22) + "…" : order.tour },
                    { lbl: "ДАТЫ", val: fmtDate(order.date_start) + (order.date_end ? " — " + fmtDate(order.date_end) : "") },
                    { lbl: "ГОСТЕЙ", val: order.participants_count ? `${order.participants_count} чел.` : "—" },
                    { lbl: "МЕНЕДЖЕР", val: order.manager },
                ].map(({ lbl, val }) => (
                    <div key={lbl} className="td-hfield">
                        <span className="td-hlabel">{lbl}</span>
                        <span title={order.tour} className="td-hvalue">{val || "—"}</span>
                    </div>
                ))}

                <div className="td-htype">
                    <span className="td-hlabel">ТИП</span>
                    <span className="td-badge" style={{ background: badge.color }}>{badge.label}</span>
                </div>

                {
                    order.status === 1
                        ? (
                            <div className="td-htype">
                                <span className="td-hlabel">Статус</span>
                                <span className="td-badge" style={{ background: `red` }}>Отменено</span>
                            </div>
                        )
                        : null
                }
                <div className="td-htype">
                    {(user?.fullname === order.manager || (user?.fullname === "admin")) && (
                        order.status === 0
                            ? (
                                <button className="orow__btn_status true" onClick={() => handleUpdate(order.order_number, 1)}>Отменить</button>
                            )
                            : order.status === 1 ? (
                                <button className="orow__btn_status false" onClick={() => handleUpdate(order.order_number, 0)}>Активировать</button>
                            ) : null
                    )}
                </div>
                <div className="td-pdf">
                    {["ru", "en"].map((lang) => (
                        <button
                            key={lang}
                            className="td-pdf-btn"
                            disabled={Boolean(pdfLoading)}
                            onClick={() => handlePdf(lang)}
                            title={lang === "ru" ? "Скачать PDF на русском" : "Download PDF in English"}
                        >
                            <i className={`fas ${pdfLoading === lang ? "fa-spinner fa-spin" : "fa-file-pdf"}`} />
                            {lang.toUpperCase()}
                        </button>
                    ))}
                </div>
                <div className="td-editTour">
                    {(user?.fullname === order.manager || (user?.fullname === "admin")) && (
                        <Link to={`/edittour/${order.order_number}`} className="link td-editTour-button">
                            Редактировать
                        </Link>
                    )}
                </div>
            </div>

            {/* ── Content ── */}
            <div className="td-content">

                {/* 3 cards */}
                <div className="td-cards">
                    <Card1 type={type} order={order} guests={guests} food={food} services={services} />

                    {/* Даты — общая для всех типов */}
                    <Card faIcon="fa-calendar-alt" title="Даты Тура">
                        <div className="td-g2">
                            <Field label="ДАТА НАЧАЛА" value={fmtDate(order.date_start)} bold />
                            <Field label="ДАТА ОКОНЧАНИЯ" value={fmtDate(order.date_end)} />
                            <Field label="РАЗМЕЩЕНИЕ В ЮРТЕ" value={order.needs_yurt ? (order.yurt_comment || "Да") : "Не нужна"} />
                        </div>
                        {extras.length > 0 && (
                            <div className="td-card-extras">
                                <div className="td-label">ДОП. УСЛУГИ</div>
                                {extras.map((x, i) => (
                                    <div key={i} className="td-extra-row">
                                        <span>{x.title}</span>
                                        <strong>${fmtMoney(x.total)}</strong>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>

                    <Card3 type={type} order={order} guests={guests} />
                </div>

                <ProgramSection type={type} order={order} food={food} />
                <PaymentSection order={order} services={services} extras={extras} />
                <GuestsTable type={type} guests={guests} />

                {order.comment && (
                    <div className="td-cmt">
                        <div className="td-cmt-head">
                            <i className="fas fa-comment-dots" />
                            <div className="td-label">ДОПОЛНИТЕЛЬНЫЕ КОММЕНТАРИИ</div>
                        </div>
                        <p className="td-cmt-text">{order.comment}</p>
                    </div>
                )}

                <div className="td-footer">
                    <span><i className="fas fa-clock" />Создан: {fmtDate(order.created_at)}</span>
                    <span><i className="fas fa-hashtag" />№ Заказа: {order.order_number}</span>
                </div>

            </div>
        </div>
    );
};

export default TourDetails;